import { useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Field, JsonEditor, Pill, Spinner, useAsync } from '../components/Common';
import { formatDateTime } from '../format';

interface ReportListItem {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  version: number;
  updated_at: string;
}

interface ReportDetail extends ReportListItem {
  params: any[];
  sources: any[];
  layout: any[];
  visibility: { roles?: string[]; teams?: string[]; userIds?: number[] };
  enabled: boolean;
}

/**
 * Report builder.
 *
 * Structured JSON editing rather than a drag-and-drop canvas. That is a
 * deliberate trade: a report definition here is a versioned, diffable,
 * restorable artefact, and every save snapshots the previous version. A visual
 * canvas would be friendlier for a first report and materially worse for the
 * thing that actually matters — being able to answer "what changed, when, and by
 * whom" when a number moves.
 */
export function ReportEditor({ slug, onNavigate }: { slug?: string; onNavigate: (path: string) => void }) {
  const list = useAsync(() => api.get<ReportListItem[]>('/api/reports'));
  const detail = useAsync(() => (slug ? api.get<ReportDetail>(`/api/reports/${slug}`) : Promise.resolve(null)), [slug]);
  const requests = useAsync(() => api.get<{ slug: string; name: string }[]>('/api/requests'));

  if (!slug) {
    if (list.loading && !list.data) return <Spinner label="Loading reports…" />;
    return (
      <>
        <div className="page-head">
          <div>
            <h1>Report builder</h1>
            <p>Reports are composed of data sources (requests and managed lists) and presentation blocks.</p>
          </div>
          <div className="page-actions">
            <button className="primary" onClick={() => onNavigate('editor/__new')}>
              New report
            </button>
          </div>
        </div>
        <div className="card">
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Category</th>
                  <th className="right">Version</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(list.data ?? []).map((report) => (
                  <tr key={report.slug}>
                    <td>
                      <strong>{report.name}</strong>
                      <div className="mono" style={{ color: 'var(--muted)' }}>
                        {report.slug}
                      </div>
                    </td>
                    <td>{report.category ?? '—'}</td>
                    <td className="right">v{report.version}</td>
                    <td>{formatDateTime(report.updated_at)}</td>
                    <td className="right">
                      <button className="small" onClick={() => onNavigate(`editor/${report.slug}`)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  const isNew = slug === '__new';
  if (!isNew && detail.loading && !detail.data) return <Spinner label="Loading report…" />;
  if (!isNew && detail.error) return <Banner tone="error">{detail.error}</Banner>;

  return (
    <Editor
      report={isNew ? null : detail.data!}
      requestSlugs={(requests.data ?? []).map((r) => r.slug)}
      onDone={(nextSlug) => onNavigate(nextSlug ? `editor/${nextSlug}` : 'editor')}
    />
  );
}

const STARTER_LAYOUT = [
  { type: 'kpi', title: 'Headline', items: [{ label: 'Rows', source: 'main', agg: { fn: 'count' }, format: 'integer' }] },
  { type: 'table', title: 'Detail', source: 'main' },
];

function Editor({
  report,
  requestSlugs,
  onDone,
}: {
  report: ReportDetail | null;
  requestSlugs: string[];
  onDone: (slug?: string) => void;
}) {
  const [form, setForm] = useState({
    name: report?.name ?? '',
    slug: report?.slug ?? '',
    description: report?.description ?? '',
    category: report?.category ?? 'General',
    enabled: report?.enabled ?? true,
  });
  const [params, setParams] = useState<any[]>(report?.params ?? []);
  const [sources, setSources] = useState<any[]>(report?.sources ?? [{ key: 'main', type: 'request', request_slug: '' }]);
  const [layout, setLayout] = useState<any[]>(report?.layout ?? STARTER_LAYOUT);
  const [visibility, setVisibility] = useState<any>(report?.visibility ?? { roles: ['admin', 'editor', 'viewer'] });
  const [valid, setValid] = useState({ params: true, sources: true, layout: true, visibility: true });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const versions = useAsync(
    () => (report ? api.get<any[]>(`/api/reports/${report.id}/versions`) : Promise.resolve([])),
    [report?.id, saved],
  );

  // Catch the most common definition error — a block pointing at a source that
  // does not exist — before it becomes a run-time warning on a manager's screen.
  const sourceKeys = sources.map((s) => s.key);
  const danglingBlocks = layout
    .filter((b) => b.source && !sourceKeys.includes(b.source))
    .map((b) => `Block “${b.title ?? b.type}” uses unknown source “${b.source}”.`);
  const danglingKpis = layout
    .filter((b) => b.type === 'kpi')
    .flatMap((b) => (b.items ?? []).filter((i: any) => i.source && !sourceKeys.includes(i.source)).map((i: any) => `KPI “${i.label}” uses unknown source “${i.source}”.`));
  const danglingRequests = sources
    .filter((s) => (s.type ?? 'request') === 'request' && s.request_slug && !requestSlugs.includes(s.request_slug))
    .map((s) => `Source “${s.key}” references request “${s.request_slug}”, which does not exist.`);
  const problems = [...danglingRequests, ...danglingBlocks, ...danglingKpis];

  const save = async () => {
    if (Object.values(valid).some((v) => !v)) {
      setError('Fix the invalid JSON before saving.');
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const payload = { ...form, params, sources, layout, visibility };
      if (report) {
        const res = await api.put<{ version: number }>(`/api/reports/${report.id}`, payload);
        setSaved(`Saved as version ${res.version}.`);
      } else {
        const res = await api.post<{ slug: string }>('/api/reports', payload);
        onDone(res.slug);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (version: number) => {
    if (!report) return;
    setBusy(true);
    try {
      await api.post(`/api/reports/${report.id}/restore/${version}`);
      onDone(report.slug);
      window.location.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{report ? report.name : 'New report'}</h1>
          <p>
            Sources run in the order listed — a source that joins another must come after it. Every save creates a
            restorable version.
          </p>
        </div>
        <div className="page-actions">
          {report && (
            <a className="btn" href={`#/reports/${report.slug}`}>
              Run it
            </a>
          )}
          <button onClick={() => onDone()}>Back to list</button>
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {saved && <Banner tone="good">{saved}</Banner>}
      {problems.length > 0 && <Banner tone="warn" title="This definition has loose ends" items={problems} />}

      <div className="split">
        <div className="card">
          <h3>Identity</h3>
          <div className="row">
            <Field label="Name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Category" help="Groups the report on the catalogue screen.">
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </Field>
          </div>
          <Field label="Description" help="Shown to managers on the catalogue and at the top of the report.">
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            Available to run
          </label>

          <JsonEditor
            label="Who can run it"
            help='{"roles":["viewer"],"teams":["Education"],"userIds":[]} — an empty list means no restriction on that dimension. Admins always have access.'
            value={visibility}
            rows={4}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, visibility: ok }));
              if (ok) setVisibility(parsed ?? {});
            }}
          />

          <JsonEditor
            label="Parameters shown to the person running it"
            value={params}
            rows={8}
            help='e.g. [{"name":"dateRange","label":"Period","type":"daterange","required":true,"default":{"preset":"this_month"}}]'
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, params: ok }));
              if (ok) setParams(Array.isArray(parsed) ? parsed : []);
            }}
          />
        </div>

        <div className="card">
          <h3>Data and presentation</h3>
          <JsonEditor
            label="Data sources"
            rows={12}
            help={`Requests available: ${requestSlugs.join(', ') || 'none yet'}. A lookup source uses {"type":"lookup","lookup_slug":"consultants"}. Set "required": false to let the report render without a source that may be unavailable.`}
            value={sources}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, sources: ok }));
              if (ok) setSources(Array.isArray(parsed) ? parsed : []);
            }}
          />
          <JsonEditor
            label="Layout blocks"
            rows={16}
            help="Block types: kpi, funnel, bar, line, table, text. Charts share one axis by design — for a second measure of a different scale, add a second chart rather than a second axis."
            value={layout}
            onChange={(parsed, ok) => {
              setValid((v) => ({ ...v, layout: ok }));
              if (ok) setLayout(Array.isArray(parsed) ? parsed : []);
            }}
          />
        </div>
      </div>

      {report && (versions.data ?? []).length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Version history</h3>
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Saved</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(versions.data ?? []).map((v) => (
                  <tr key={v.version}>
                    <td>
                      v{v.version} {v.version === report.version && <Pill tone="ok">current</Pill>}
                    </td>
                    <td>{formatDateTime(v.created_at)}</td>
                    <td>{v.author ?? '—'}</td>
                    <td className="right">
                      {v.version !== report.version && (
                        <button className="small" onClick={() => restore(v.version)} disabled={busy}>
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
